import type { FetchResult } from '@apollo/client';
import { useMutation } from '@apollo/client';
import * as Sentry from '@sentry/react';
import gql from 'graphql-tag';

import { simplifyQueryValue } from 'js/lib/queryUtils';
import { useLocation, useParams } from 'js/lib/useRouter';

import { useTracker } from '@coursera/event-pulse/react';
import type {
  SendVerificationEmailRequest,
  SendVerificationEmailResponse,
} from '@coursera/grpc-types-useremails/coursera/proto/useremails/v1beta1/user_emails_api';

import { ADMIN_ROLE_FROM_TEMPLATE_ID } from 'bundles/enterprise-admin-management/constants';
import AcceptAllInvitationsForEmailAndOrganizationFromInvitation from 'bundles/enterprise-admin-onboarding/mutations/AcceptAllInvitationsForEmailAndOrganizationFromInvitation.graphql';

import type {
  AcceptAllInvitationsForEmailAndOrganizationFromInvitationMutation as AcceptAllInvitationsForEmailAndOrganizationFromInvitationMutationData,
  AcceptAllInvitationsForEmailAndOrganizationFromInvitationMutationVariables,
} from './mutations/__generated__/AcceptAllInvitationsForEmailAndOrganizationFromInvitation';

export const sendVerificationEmailMutation = gql`
  mutation sendVerificationEmailMutation($input: DataMap!) {
    sendVerificationEmail(input: $input)
      @rest(
        type: "SendVerificationEmailResponse"
        path: "grpc/useremails/v1beta1/UserEmailsAPI/SendVerificationEmail"
        method: "POST"
      ) {
      __typename
    }
  }
`;

export type PropsFromUseInviteAcceptance = {
  sendVerificationEmail: (request: {
    variables: {
      input: SendVerificationEmailRequest;
    };
  }) => Promise<
    FetchResult<{
      sendVerificationEmail: SendVerificationEmailResponse;
    }>
  >;
  acceptAllInvitationsForEmailAndOrganization: (request: {
    variables: AcceptAllInvitationsForEmailAndOrganizationFromInvitationMutationVariables;
  }) => Promise<FetchResult<AcceptAllInvitationsForEmailAndOrganizationFromInvitationMutationData>>;
  onInviteAccepted: () => void;
};

export const useInviteAcceptance = (): PropsFromUseInviteAcceptance => {
  const [acceptAllInvitationsForEmailAndOrganization] = useMutation<
    AcceptAllInvitationsForEmailAndOrganizationFromInvitationMutationData,
    AcceptAllInvitationsForEmailAndOrganizationFromInvitationMutationVariables
  >(AcceptAllInvitationsForEmailAndOrganizationFromInvitation, {
    context: { clientName: 'gatewayGql' },
    onError: (error) => {
      if (error.graphQLErrors[0]?.extensions?.classification === 'ValidationError') {
        Sentry.captureException(error, {
          extra: { message: 'Failed to accept an admin invite because of validation' },
        });
      }
      throw error; // to render the error state
    },
  });
  const [sendVerificationEmail] = useMutation<
    {
      sendVerificationEmail: SendVerificationEmailResponse;
    },
    {
      input: SendVerificationEmailRequest;
    }
  >(sendVerificationEmailMutation);
  const { thirdPartySlug } = useParams();
  const {
    query: { roleTemplateId },
  } = useLocation();
  const track = useTracker();
  const onInviteAccepted = () =>
    track('click_button', {
      button: {
        name: 'enterprise_admin_join',
        linkName: ADMIN_ROLE_FROM_TEMPLATE_ID[simplifyQueryValue(roleTemplateId) ?? ''] ?? roleTemplateId, // TODO handle null/undefined case
      },
      enterprise: {
        organizationSlug: thirdPartySlug,
      },
    });
  return {
    acceptAllInvitationsForEmailAndOrganization,
    sendVerificationEmail,
    onInviteAccepted,
  };
};
